import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import { db } from "./firebase";
import { doc, getDoc, setDoc, addDoc, collection, getDocs, query, orderBy, limit } from "firebase/firestore";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading, null = logged out

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        // Record one login event per browser session to avoid duplicate entries on page refresh
        const sessionKey = `login_recorded_${firebaseUser.uid}`;
        if (!sessionStorage.getItem(sessionKey)) {
          sessionStorage.setItem(sessionKey, "1");
          const now = new Date().toISOString();
          try {
            await addDoc(
              collection(db, "users", firebaseUser.uid, "logins"),
              { timestamp: now }
            );
            const userDocRef = doc(db, "users", firebaseUser.uid);
            const userSnap = await getDoc(userDocRef);
            const updates = { lastLogin: now };
            if (!userSnap.exists() || !userSnap.data()?.joinedAt) {
              // Use the Firebase Auth account creation time as the joined date
              // (more accurate than "now", especially for users who existed
              // before this field was introduced)
              const authCreated = new Date(firebaseUser.metadata.creationTime).toISOString();
              // If there's an earlier login record already in Firestore, prefer that
              const loginsSnap = await getDocs(
                query(
                  collection(db, "users", firebaseUser.uid, "logins"),
                  orderBy("timestamp"),
                  limit(1)
                )
              );
              if (!loginsSnap.empty) {
                const firstTimestamp = loginsSnap.docs[0].data().timestamp;
                updates.joinedAt = firstTimestamp < authCreated ? firstTimestamp : authCreated;
              } else {
                updates.joinedAt = authCreated;
              }
            }
            await setDoc(userDocRef, updates, { merge: true });
          } catch (e) {
            // login tracking is non-critical, fail silently
          }
        }
      }
    });
    return () => unsubscribe();
  }, []);

  return <AuthContext.Provider value={user}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
